<template>
    <ValidationErrors :show="showErrors"/>
    <Head :title="'Expenses - ' + fixture.title"/>
    <Audit v-if="openModal" @close="openModal = false" :open="openModal" :activity_logs="activity_logs" />
    <div class="w-full mb-6">
        <FixtureHeader :fixture="fixture"></FixtureHeader>
        <div class="space-y-6 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <div class="flex pb-5 border-b border-gray-200 justify-between items-center">
                                <h3 class="text-lg leading-6 font-bold text-gray-900">Expenses</h3>
                                <div class="flex text-right items-end">
                                    <Button type="button" @click="emitter.emit('openExpenseSlideout', fixture)"
                                            class="shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  stroke-width="2"
                                                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Create New Expense
                                    </Button>
                                    <a :href="route('frontend.fixture.expenses.export', {
                                        fixture: fixture.id,
                                        _query: {
                                            starts_at: this.form.starts_at,
                                            ends_at: this.form.ends_at,
                                            search: this.form.search
                                        }})" class="shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ml-2 font-semibold text-xs text-white uppercase tracking-widest">
                                        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  stroke-width="2"
                                                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Export Expenses
                                    </a>
                                </div>
                            </div>
                            <div class="grid grid-cols-3 gap-2">
                                <div class="relative">
                                    <label  for="start-date" class="block text-sm font-medium text-gray-700">Start Date <span v-if="formattedStartDate">({{ formattedStartDate }})</span></label>
                                    <input v-model="form.starts_at" name="start-date" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select start date">
                                </div>
                                <div class="relative">
                                    <label for="end-date" class="block text-sm font-medium text-gray-700">End Date <span v-if="formattedEndDate">({{ formattedEndDate }})</span></label>
                                    <input v-model="form.ends_at" name="end-date" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select end date">
                                </div>
                                <div class="relative">
                                    <label for="end-date" class="block text-sm font-medium text-gray-700 px-4">Search Expenses (Description, Supplier, Employee)</label>
                                    <search-filter :show-filter="false" v-model:value="form.search" class="mt-2 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full" @reset="reset"></search-filter>
                                </div>
                            </div>
                            <div class="text-center py-4 lg:px-4">
                                <div class="p-2 bg-light-pitch-purple items-center text-white leading-none lg:rounded-md flex lg:inline-flex" role="alert">
                                    <span class="flex rounded-full bg-dark-pitch-purple uppercase px-2 py-1 text-xs font-bold mr-3">
                                        <InformationCircleIcon class="w-5 h-5 text-white"></InformationCircleIcon>
                                    </span>
                                    <span class="mr-2 text-left text-sm uppercase font-bold flex-auto text-white">Current page {{ this.fixture.primary_trading_details?.currency?.code ?? 'GBP' }} total {{ formatCurrency(paginated_expenses_total_amount, this.fixture.primary_trading_details?.currency?.code ?? 'GBP', true) }}, of total {{ this.fixture.primary_trading_details?.currency?.code ?? 'GBP' }} amount {{ formatCurrency(expenses_total_amount, this.fixture.primary_trading_details?.currency?.code ?? 'GBP', true) }}.</span>
                                </div>
                            </div>
                            <div class="flex flex-col mt-3 space-y-3">
                                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8"  :class="{'flex flex-col h-screen' : expenses.data.length > 8}">
                                        <div class="shadow border-b border-gray-200 sm:rounded-lg" :class="{'flex-grow overflow-auto' : expenses.data.length > 8}">
                                            <table class="min-w-full divide-y divide-gray-200" :class="{'relative w-full' : expenses.data.length > 8}">
                                                <thead class="bg-gray-50 sticky top-0">
                                                    <tr>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Description
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Supplier
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Employee Name
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Payment Date
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Amount
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            {{ this.fixture.primary_trading_details.currency.code }} Amount
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Expense Type
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Transaction Type
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Nominal
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Cost Center
                                                        </th>
                                                        <th scope="col" class="relative px-6 py-3">
                                                            <span class="sr-only">Edit</span>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-if="expenses.data.length > 0"
                                                        v-for="(expense, index) in expenses.data" :key="index"
                                                        :class="{'bg-gray-100': index % 2}"
                                                        class="hover:bg-gray-50 cursor-pointer">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ decodeEntities(expense.description?.replace(/<\/?[^>]+>/ig, " ")) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                                                v-html="expense.supplier">
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ expense.user.name }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ moment(expense.paid_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ formatCurrency(expense.amount, expense.currency.code ?? 'GBP' , true) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ formatCurrency(expense.gbp_amount, this.fixture.primary_trading_details?.currency?.code ?? 'GBP', true) }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ expense.expense_type.name }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex flex-col">
                                                                {{ expense.transaction_type.name }}
                                                                <span class="w-full text-gray-400">{{ expense.transaction_type.title }}</span>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ expense.transaction_type.nominal }}
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ expense.transaction_type.cost_center }}
                                                            </td>
                                                            <td class="px-6  py-4 whitespace-nowrap text-right text-sm flex">
                                                                <PencilAltIcon @click="emitter.emit('editExpenseSlideout', expense)"
                                                                            class="w-4 h-4 mr-3"></PencilAltIcon>
                                                                <TrashIcon @click="deleteExpense(expense.id)"
                                                                        class="w-4 h-4 text-red-500"></TrashIcon>
                                                            </td>
                                                    </tr>
                                                    <tr v-else>
                                                        <td colspan="9"
                                                            class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">
                                                            There are no expenses available for your search criteria.
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <Pagination :links="expenses.links"/>
                            </div>

                            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
</template>


<script>
import moment from "moment";
import {
    ClipboardCopyIcon,
    PlusSmIcon,
    UsersIcon,
    InformationCircleIcon,
    ChevronDownIcon,
    CogIcon,
    PencilAltIcon,
    CheckCircleIcon,
    TrashIcon,
} from "@heroicons/vue/outline";
import pickBy from "lodash/pickBy";
import {emitter} from "@/eventHub";
import debounce from "lodash/debounce";
import Button from "@/Components/Button";
import { router } from '@inertiajs/vue3';
import mapValues from "lodash/mapValues";
import Pagination from "@/Components/Pagination";
import Authenticated from "@/Layouts/Authenticated";
import {Link, Head, useForm} from "@inertiajs/vue3";
import SearchFilter from "@/Components/SearchFilter";
import FixtureHeader from "@/Components/FixtureHeader";
import ValidationErrors from "@/Components/ValidationErrors";
import CreateQuote from "@/Components/SlideOuts/CreateQuote";
import CreateExpense from "@/Components/SlideOuts/CreateExpense";
import DuplicateQuote from "@/Components/SlideOuts/DuplicateQuote";
import {Popover, PopoverButton, PopoverGroup} from "@headlessui/vue";
import Audit from "@/Components/Modal/Audit.vue";

export default {
    layout: Authenticated,
    components: {
        CreateExpense,
        DuplicateQuote,
        Button,
        CreateQuote,
        FixtureHeader,
        ValidationErrors,
        Link,
        Head,
        PlusSmIcon,
        UsersIcon,
        InformationCircleIcon,
        Popover,
        PopoverButton,
        ChevronDownIcon,
        PopoverGroup,
        CogIcon,
        PencilAltIcon,
        ClipboardCopyIcon,
        CheckCircleIcon,
        Pagination,
        TrashIcon,
        SearchFilter,
        Audit
    },
    props: {
        fixture: Object,
        expenses: Object,
        expenses_count: Number,
        expenses_total_amount: Number,
        paginated_expenses_total_amount: Number,
        filters: Array || null,
        activity_logs: Array
    },
    data() {
        return {
            showErrors: false,
            emitter: emitter,
            openModal: false,
            form: useForm({
                starts_at: this.filters.starts_at ?? '',
                ends_at: this.filters.ends_at ?? '',
                search: this.filters.search ?? '',
            }),
        }
    },
    computed: {
        formattedStartDate() {
            if (this.form.starts_at) {
                return moment(this.form.starts_at).format('DD MMM YYYY');
            }
            return '';
        },
        formattedEndDate() {
            if (this.form.ends_at) {
                return moment(this.form.ends_at).format('DD MMM YYYY');
            }
            return '';
        }
    },
    created() {
        this.moment = moment
    },
    methods: {
        reset() {
            this.form.search = null;
        },
        deleteExpense(id) {
            this.$swal.fire({
                title: 'Remove Expense',
                html: "You are removing an expense. <br/>Are you sure you'd like to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Delete the country
                        this.$inertia.delete(route('frontend.expenses.expense.delete', id), {
                            preserveScroll: true
                        });
                    }
                });
        },
        decodeEntities(encodedString) {
            const parser = new DOMParser();
            const dom = parser.parseFromString(`<!doctype html><body>${encodedString}`, 'text/html');
            return dom.body.textContent;
        }
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function() {
                this.$inertia.get(route('frontend.fixture.expenses.index', this.fixture.id), pickBy(this.form), {preserveState: true, preserveScroll: true})
            }, 500),
        }
    }
}
</script>
